import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/FormPage.css';

function CreateVendor() {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(Boolean(vendorId));
    const [vendorTypes, setVendorTypes] = useState([]);
    const [projects, setProjects] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        vendorTypeId: '',
        mobileNumber: '',
        email: '',
        address: '',
        projectId: ''
    });

    useEffect(() => {
        withLoading(async () => {
            const typesRes = await api.get('/vendor-types');
            setVendorTypes(typesRes.data);

            const projectsRes = await api.get('/projects');
            setProjects(projectsRes.data);

            if (vendorId) {
                const vendorRes = await api.get(`/vendors/${vendorId}`);
                setFormData({
                    name: vendorRes.data.name || '',
                    vendorTypeId: String(vendorRes.data.vendorTypeId || ''),
                    mobileNumber: vendorRes.data.mobileNumber || '',
                    email: vendorRes.data.email || '',
                    address: vendorRes.data.address || '',
                    projectId: vendorRes.data.projectId ? String(vendorRes.data.projectId) : ''
                });
            }
        }).catch((error) => {
            const message = error.response?.data?.Error || error.message || 'Error loading vendor data';
            alert(message);
        });
    }, [vendorId, withLoading]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            name: formData.name.trim(),
            vendorTypeId: Number(formData.vendorTypeId),
            mobileNumber: formData.mobileNumber.trim(),
            email: formData.email.trim() || null,
            address: formData.address.trim() || null,
            projectId: formData.projectId ? Number(formData.projectId) : null
        };

        const apiCall = vendorId
            ? api.put(`/vendors/${vendorId}`, payload)
            : api.post('/vendors', payload);

        apiCall
            .then(() => {
                navigate('/admin/vendors');
            })
            .catch((error) => {
                const message = error.response?.data?.Error || 'Error saving vendor details';
                alert(message);
            });
    };

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="form-page">
                <div className="form-page-header">
                    <div>
                        <h1>{vendorId ? 'Edit vendor' : 'Add vendor'}</h1>
                        <p className="form-page-subtitle">
                            {vendorId
                                ? 'Update vendor details and linked project'
                                : 'Enter vendor details to add them to your records'}
                        </p>
                    </div>
                    <div className="form-page-actions">
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => navigate('/admin/vendors')}
                        >
                            Back to list
                        </button>
                        <button
                            type="submit"
                            form="create-vendor-form"
                            className="form-page-btn"
                        >
                            {vendorId ? 'Update vendor' : 'Save vendor'}
                        </button>
                    </div>
                </div>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Vendor details</h2>
                            <p className="form-page-section-hint">Vendor name, type, and contact information</p>
                        </div>
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => navigate('/admin/vendor-types')}
                        >
                            Manage vendor types
                        </button>
                    </div>

                    <form id="create-vendor-form" onSubmit={handleSubmit}>
                        <div className="form-page-grid cols-3">
                            <div className="form-page-field span-full">
                                <label htmlFor="name">Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter vendor name"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="vendorTypeId">Vendor type</label>
                                <select
                                    id="vendorTypeId"
                                    name="vendorTypeId"
                                    value={formData.vendorTypeId}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select vendor type</option>
                                    {vendorTypes.map(vendorType => (
                                        <option key={vendorType.id} value={vendorType.id}>
                                            {vendorType.type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="projectId">Project (optional)</label>
                                <select
                                    id="projectId"
                                    name="projectId"
                                    value={formData.projectId}
                                    onChange={handleChange}
                                >
                                    <option value="">No project</option>
                                    {projects.map(project => (
                                        <option key={project.id} value={project.id}>
                                            {project.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="mobileNumber">Mobile number</label>
                                <input
                                    type="tel"
                                    id="mobileNumber"
                                    name="mobileNumber"
                                    value={formData.mobileNumber}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter mobile number"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="email">Email (optional)</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Enter email address"
                                />
                            </div>
                            <div className="form-page-field span-full">
                                <label htmlFor="address">Address (optional)</label>
                                <textarea
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Enter vendor address"
                                />
                            </div>
                        </div>
                    </form>
                </section>
            </div>
        </PageLoadingArea>
    );
}

export default CreateVendor;
